import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const biologyUnit6Notes: UnitNotes = {
  unitId: "unit-6",
  unitName: "Unit 6: Gene Expression and Regulation",
  examWeight: "12–16%",
  focus: "Information Storage and Transmission",
  sections: [
    {
      id: "topic-6-1",
      title: "Topic 6.1: DNA and RNA Structure",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 Structure of Nucleic Acids</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Genetic information is stored in nucleic acids (DNA and RNA). Both are polymers composed of monomers called <strong>nucleotides</strong>.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <h4 className="font-bold text-slate-900 mb-2">Nucleotide Components:</h4>
              <ul className="grid md:grid-cols-3 gap-4">
                <li className="bg-white p-3 rounded shadow-sm border border-blue-100">
                  <span className="font-bold text-blue-600">1. Phosphate Group:</span>
                  <p className="text-xs text-slate-600 mt-1">Negatively charged; attached to the <InlineMath math="5'" /> carbon of the sugar.</p>
                </li>
                <li className="bg-white p-3 rounded shadow-sm border border-green-100">
                  <span className="font-bold text-green-600">2. Pentose Sugar:</span>
                  <p className="text-xs text-slate-600 mt-1">5-carbon sugar (Deoxyribose in DNA; Ribose in RNA).</p>
                </li>
                <li className="bg-white p-3 rounded shadow-sm border border-purple-100">
                  <span className="font-bold text-purple-600">3. Nitrogenous Base:</span>
                  <p className="text-xs text-slate-600 mt-1">Attached to the <InlineMath math="1'" /> carbon of the sugar.</p>
                </li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <KeyTerm term="Purines" definition="Double-ring structure. Includes Adenine (A) and Guanine (G)." />
                <KeyTerm term="Pyrimidines" definition="Single-ring structure. Includes Cytosine (C), Thymine (T), and Uracil (U)." />
              </div>
              <Mnemonic title="Nitrogenous Bases" content="Pure As Gold (Purines: A, G) | Cut The Pye (Pyrimidines: C, T, U)" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔗 Base Pairing and Stability</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <ul className="space-y-2 text-sm text-slate-700">
                <li><strong>A pairs with T</strong> (DNA) or <strong>U</strong> (RNA) via <InlineMath math="2" /> hydrogen bonds.</li>
                <li><strong>G pairs with C</strong> via <InlineMath math="3" /> hydrogen bonds.</li>
              </ul>
              <div className="mt-3 p-2 bg-white rounded border border-blue-100 text-xs italic text-slate-600">
                Significance: DNA with higher G-C content is more stable and requires higher temperatures to denature due to the extra hydrogen bond.
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 DNA vs. RNA</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-slate-200 rounded-lg">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700">Feature</th>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700">DNA</th>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700">RNA</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-t border-slate-100">
                    <td className="px-4 py-2 font-semibold bg-slate-50">Sugar</td>
                    <td className="px-4 py-2">Deoxyribose (lacks O at <InlineMath math="2'" />)</td>
                    <td className="px-4 py-2">Ribose (has O at <InlineMath math="2'" />)</td>
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="px-4 py-2 font-semibold bg-slate-50">Bases</td>
                    <td className="px-4 py-2">A, G, C, <strong>Thymine (T)</strong></td>
                    <td className="px-4 py-2">A, G, C, <strong>Uracil (U)</strong></td>
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="px-4 py-2 font-semibold bg-slate-50">Strands</td>
                    <td className="px-4 py-2">Double-stranded (Helix)</td>
                    <td className="px-4 py-2">Typically single-stranded</td>
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="px-4 py-2 font-semibold bg-slate-50">Directionality</td>
                    <td className="px-4 py-2">Antiparallel</td>
                    <td className="px-4 py-2">Linear <InlineMath math="5' \text{ to } 3'" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📐 Directionality and Genome Organization</h3>
            <div className="space-y-3">
              <p className="text-sm text-slate-700"><strong>Antiparallel:</strong> Strands run in opposite directions. <InlineMath math="5'" /> end terminates with a phosphate; <InlineMath math="3'" /> end terminates with a hydroxyl (-OH). New nucleotides can <strong>ONLY</strong> be added to the <InlineMath math="3'" /> end.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                  <h4 className="font-bold text-teal-900 mb-1">Prokaryotic Genome</h4>
                  <p className="text-xs text-slate-700">Circular chromosomes in the nucleoid; contains <strong>Plasmids</strong> (small, extra-chromosomal DNA).</p>
                </div>
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h4 className="font-bold text-purple-900 mb-1">Eukaryotic Genome</h4>
                  <p className="text-xs text-slate-700">Multiple linear chromosomes in the nucleus; DNA wrapped around <strong>histone</strong> proteins to form nucleosomes (chromatin).</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-2",
      title: "Topic 6.2: DNA Replication",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 The Semiconservative Model</h3>
            <p className="text-slate-700 leading-relaxed">
              One strand of parental DNA serves as a template for a new strand. The resulting DNA molecule consists of one original parent strand and one new strand (proven by the <strong>Meselson-Stahl experiment</strong>).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛠️ The Replication Machinery</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-white p-4 border border-slate-200 rounded-lg">
                <h4 className="font-bold text-blue-800">Topoisomerase</h4>
                <p className="text-xs text-slate-600">Relaxes supercoiling in front of the replication fork to prevent breakage.</p>
              </div>
              <div className="bg-white p-4 border border-slate-200 rounded-lg">
                <h4 className="font-bold text-blue-800">Helicase</h4>
                <p className="text-xs text-slate-600">Unwinds the double helix by breaking hydrogen bonds between bases.</p>
              </div>
              <div className="bg-white p-4 border border-slate-200 rounded-lg">
                <h4 className="font-bold text-blue-800">DNA Polymerase</h4>
                <p className="text-xs text-slate-600">Synthesizes new strands <strong>5' → 3'</strong>. Requires a template and an RNA primer.</p>
              </div>
              <div className="bg-white p-4 border border-slate-200 rounded-lg">
                <h4 className="font-bold text-blue-800">Ligase</h4>
                <p className="text-xs text-slate-600">Joins Okazaki fragments by forming phosphodiester bonds.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ The Replication Process</h3>
            <div className="space-y-4">
              <div className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">1</span>
                <div>
                  <h4 className="font-bold text-slate-800">Initiation</h4>
                  <p className="text-sm text-slate-600">Helicase opens the helix at origins of replication, forming a replication bubble.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">2</span>
                <div>
                  <h4 className="font-bold text-slate-800">Priming</h4>
                  <p className="text-sm text-slate-600"><strong>Primase</strong> adds a short RNA primer to provide a <InlineMath math="3'" /> -OH group.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">3</span>
                <div className="flex-grow">
                  <h4 className="font-bold text-slate-800">Elongation</h4>
                  <div className="grid md:grid-cols-2 gap-3 mt-2">
                    <div className="p-3 bg-green-50 rounded border border-green-200">
                      <h5 className="font-bold text-green-900 text-xs">Leading Strand</h5>
                      <p className="text-xs mt-1">Continuous synthesis <strong>toward</strong> the replication fork.</p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded border border-orange-200">
                      <h5 className="font-bold text-orange-900 text-xs">Lagging Strand</h5>
                      <p className="text-xs mt-1">Discontinuous synthesis <strong>away from</strong> the fork in <strong>Okazaki fragments</strong>.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">4</span>
                <div>
                  <h4 className="font-bold text-slate-800">Termination</h4>
                  <p className="text-sm text-slate-600">Primers are replaced with DNA; Ligase seals the backbone.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-3",
      title: "Topic 6.3: Transcription and RNA Processing",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📜 The Central Dogma</h3>
            <div className="flex justify-center items-center gap-4 bg-slate-50 p-6 rounded-xl border border-slate-200 font-mono text-lg shadow-inner">
              <div className="text-blue-700">DNA</div>
              <div className="text-slate-400">→</div>
              <div className="text-purple-700">RNA</div>
              <div className="text-slate-400">→</div>
              <div className="text-emerald-700">Protein</div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <KeyTerm term="Transcription" definition="Synthesis of RNA from a DNA template." />
              <KeyTerm term="Translation" definition="Synthesis of a polypeptide from mRNA." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✍️ Transcription (DNA to mRNA)</h3>
            <p className="text-slate-700 mb-4">Occurs in nucleus (eukaryotes) or cytoplasm (prokaryotes).</p>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-indigo-500">
                <h4 className="font-bold text-indigo-900">Enzymes & Strands</h4>
                <ul className="text-sm text-slate-700 mt-2 space-y-1">
                  <li><strong>RNA Polymerase:</strong> Primary enzyme; synthesizes RNA <InlineMath math="5' \rightarrow 3'" />.</li>
                  <li><strong>Template Strand:</strong> Read by polymerase (non-coding/minus/antisense).</li>
                  <li><strong>Coding Strand:</strong> Not read; matches RNA sequence (except T vs U).</li>
                </ul>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                <h4 className="font-bold text-indigo-900 mb-2">The Three Steps:</h4>
                <ol className="list-decimal pl-5 text-sm space-y-2 text-slate-700">
                  <li><strong>Initiation:</strong> Polymerase binds to the <strong>Promoter</strong> (e.g., TATA box). Transcription factors mediate this.</li>
                  <li><strong>Elongation:</strong> Polymerase unwinds DNA and adds nucleotides to the <InlineMath math="3'" /> end.</li>
                  <li><strong>Termination:</strong> Polymerase reaches a terminator sequence and detaches.</li>
                </ol>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✂️ RNA Processing (Eukaryotes Only)</h3>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900 text-sm">5' GTP Cap</h4>
                <p className="text-xs text-slate-700 mt-1">Modified guanine; helps ribosome attachment and protection.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900 text-sm">3' Poly-A Tail</h4>
                <p className="text-xs text-slate-700 mt-1">Chain of Adenines; stability and export help.</p>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900 text-sm">Splicing</h4>
                <p className="text-xs text-slate-700 mt-1"><strong>Introns</strong> (intervening) cut out; <strong>Exons</strong> (expressed) joined.</p>
              </div>
            </div>
            <ImportantNote>
              <strong>Alternative Splicing:</strong> A single gene can code for multiple proteins depending on which exons are kept or removed. This increases genetic diversity without increasing genome size.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-4",
      title: "Topic 6.4: Translation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧪 Components of Translation</h3>
            <div className="space-y-3">
              <KeyTerm term="mRNA" definition="Messenger RNA; carries code in triplets called codons." />
              <KeyTerm term="tRNA" definition="Transfer RNA; carries amino acids; contains an anticodon." />
              <KeyTerm term="rRNA" definition="Ribosomal RNA; makes up the ribosome." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 The Translation Process</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4">
              <div>
                <h4 className="font-bold text-slate-900">1. Initiation</h4>
                <p className="text-sm text-slate-700">Small subunit binds mRNA at start codon (<strong>AUG</strong> - Methionine). Large subunit attaches.</p>
              </div>
              <div>
                <h4 className="font-bold text-slate-900">2. Elongation (The 3 Sites)</h4>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div className="p-2 bg-blue-100 rounded text-center text-xs font-bold text-blue-800">A Site: Accept incoming tRNA</div>
                  <div className="p-2 bg-blue-200 rounded text-center text-xs font-bold text-blue-800">P Site: Hold growing peptide</div>
                  <div className="p-2 bg-blue-300 rounded text-center text-xs font-bold text-blue-800">E Site: Exit site for empty tRNA</div>
                </div>
              </div>
              <div>
                <h4 className="font-bold text-slate-900">3. Termination</h4>
                <p className="text-sm text-slate-700"><strong>Stop Codon</strong> (UAA, UAG, UGA) reached. Release factor binds and releases the polypeptide.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👾 Retroviruses</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">
                They violate the standard dogma. Contain RNA as genetic material and use <strong>Reverse Transcriptase</strong> to copy viral RNA into DNA for host genome integration.
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-5",
      title: "Topic 6.5: Regulation of Gene Expression",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🦠 Prokaryotic Regulation: Operons</h3>
            <div className="bg-slate-100 p-4 rounded-lg mb-4">
              <h4 className="font-bold text-slate-800">Operon Structure:</h4>
              <p className="text-xs text-slate-700"><strong>Promoter</strong> (RNA Pol binds), <strong>Operator</strong> (Switch; Repressor binds), <strong>Structural Genes</strong>.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-bold text-orange-900">Inducible (e.g., Lac Operon)</h4>
                <ul className="text-xs text-slate-700 mt-2 list-disc pl-4 space-y-1">
                  <li>Usually <strong>OFF</strong>.</li>
                  <li>Inducer (Lactose) binds repressor to turn it <strong>ON</strong>.</li>
                  <li>Function: Catabolic (break down lactose).</li>
                </ul>
              </div>
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="font-bold text-purple-900">Repressible (e.g., Trp Operon)</h4>
                <ul className="text-xs text-slate-700 mt-2 list-disc pl-4 space-y-1">
                  <li>Usually <strong>ON</strong>.</li>
                  <li>Corepressor (Trp) binds repressor to turn it <strong>OFF</strong>.</li>
                  <li>Function: Anabolic (stops synthesis when level is high).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧠 Eukaryotic Regulation</h3>
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 p-4 rounded-lg">
                <h4 className="font-bold text-slate-800">Epigenetics (Chromatin Structure)</h4>
                <div className="grid md:grid-cols-2 gap-3 mt-2">
                  <div className="p-3 bg-red-50 rounded text-xs"><strong>DNA Methylation:</strong> Tightens packing; reduces transcription (Gene Silencing).</div>
                  <div className="p-3 bg-green-50 rounded text-xs"><strong>Histone Acetylation:</strong> Loosens packing; promotes transcription.</div>
                </div>
              </div>
              <KeyTerm term="Transcription Factors" definition="Proteins that bind specific DNA sequences (Promoters/Enhancers) to initiate transcription." />
              <KeyTerm term="Coordinately Regulated Genes" definition="Genes on different chromosomes activated by the same specific transcription factors." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-6",
      title: "Topic 6.6: Gene Expression and Cell Specialization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ Cell Differentiation</h3>
            <p className="text-slate-700 leading-relaxed text-sm">
              All somatic cells have the <strong>same DNA</strong>. Differentiation occurs because different cells express different genes driven by specific <strong>transcription factors</strong>.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛑 Regulatory RNAs</h3>
            <div className="bg-teal-50 p-4 border border-teal-200 rounded-lg">
              <h4 className="font-bold text-teal-900">microRNAs (miRNAs) & siRNAs</h4>
              <p className="text-sm text-slate-700 mt-1">Bind to complementary mRNA sequences and degrade them or block translation (RNA interference/RNAi).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-7",
      title: "Topic 6.7: Mutations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ DNA Mutations</h3>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h4 className="font-bold text-slate-900 mb-2">Point Mutations (Substitution)</h4>
                <ul className="grid md:grid-cols-3 gap-3">
                  <li className="p-2 bg-white rounded border border-slate-200 text-xs"><strong>Silent:</strong> No amino acid change.</li>
                  <li className="p-2 bg-white rounded border border-slate-200 text-xs"><strong>Missense:</strong> Changes amino acid.</li>
                  <li className="p-2 bg-white rounded border border-slate-200 text-xs"><strong>Nonsense:</strong> Changes to STOP codon.</li>
                </ul>
              </div>
              <KeyTerm term="Frameshift" definition="Insertion/Deletion (not in multiples of 3). Shifts reading frame; catastrophic to protein." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Chromosomal & Evolutionary Context</h3>
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <KeyTerm term="Aneuploidy" definition="Abnormal chromosome number (Nondisjunction)." />
                <KeyTerm term="Polyploidy" definition="Extra sets of chromosomes (Common in plants)." />
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900 mb-2">Horizontal Gene Transfer (Prokaryotes)</h4>
                <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">
                  <li><strong>Transformation:</strong> Uptake of naked DNA.</li>
                  <li><strong>Transduction:</strong> Viral transmission of DNA.</li>
                  <li><strong>Conjugation:</strong> Cell-to-cell transfer.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-8",
      title: "Topic 6.8: Biotechnology",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧪 Laboratory Techniques</h3>
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 p-4 rounded-lg">
                <h4 className="font-bold text-blue-900">Gel Electrophoresis</h4>
                <p className="text-xs text-slate-700 mt-1">Separates DNA/RNA/Proteins by <strong>size</strong> and <strong>charge</strong>. DNA (-) moves to (+). Smaller fragments move <strong>faster/farther</strong>.</p>
              </div>
              <div className="bg-white border border-slate-200 p-4 rounded-lg">
                <h4 className="font-bold text-emerald-900">PCR (Polymerase Chain Reaction)</h4>
                <p className="text-xs text-slate-700 mt-1">Amplification of DNA segment. Steps: <strong>Denaturation</strong> (heat) → <strong>Annealing</strong> (cool) → <strong>Extension</strong> (Taq Polymerase synthesizes).</p>
              </div>
              <div className="bg-white border border-slate-200 p-4 rounded-lg">
                <h4 className="font-bold text-amber-900">Bacterial Transformation</h4>
                <p className="text-xs text-slate-700 mt-1">Introducing plasmids. Uses heat shock and <strong>Selectable Markers</strong> (antibiotic resistance) to verify success.</p>
              </div>
              <div className="bg-white border border-slate-200 p-4 rounded-lg">
                <h4 className="font-bold text-slate-900">DNA Sequencing</h4>
                <p className="text-xs text-slate-700 mt-1">Determines precise nucleotide order. Used for evolutionary comparisons.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default biologyUnit6Notes;